import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
  CircularProgress,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import GitHubIcon from '@mui/icons-material/GitHub';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getGitHubStatus, disconnectGitHub, GitHubConnectionStatus } from '../api/github';
import { connectGitHub } from '../utils/github';
import ApiKeys from './ApiKeys';

const Settings: React.FC = () => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [githubStatus, setGithubStatus] = useState<GitHubConnectionStatus | null>(null);
  const [githubLoading, setGithubLoading] = useState(true);

  useEffect(() => {
    getGitHubStatus()
      .then(setGithubStatus)
      .catch(() => setGithubStatus({ connected: false, github_username: null }))
      .finally(() => setGithubLoading(false));
  }, []);

  const handleConnectGitHub = async () => {
    try {
      await connectGitHub();
      setGithubLoading(true);
      const s = await getGitHubStatus();
      setGithubStatus(s);
    } catch {
      // User closed popup
    } finally {
      setGithubLoading(false);
    }
  };

  const handleDisconnectGitHub = async () => {
    setGithubLoading(true);
    try {
      await disconnectGitHub();
      setGithubStatus({ connected: false, github_username: null });
    } catch {
      // ignore
    } finally {
      setGithubLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 4 }}>
      <Typography variant="h3" sx={{ mb: 1, fontWeight: 600 }}>
        Settings
      </Typography>
      <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
        Manage your account
      </Typography>

      {/* Profile Section */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          backgroundColor: '#0a0a0a',
          border: '1px solid rgba(255, 255, 255, 0.12)',
        }}
      >
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
          Profile
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Email
            </Typography>
            <Typography variant="body1">
              {user?.email || '\u2014'}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Connected Accounts */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          backgroundColor: '#0a0a0a',
          border: '1px solid rgba(255, 255, 255, 0.12)',
        }}
      >
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
          Connected Accounts
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <GitHubIcon sx={{ fontSize: 24, color: 'rgba(255, 255, 255, 0.7)' }} />
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                GitHub
              </Typography>
              {githubStatus?.connected && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CheckCircleIcon sx={{ fontSize: 14, color: '#66bb6a' }} />
                  <Typography variant="body2" sx={{ color: '#66bb6a' }}>
                    {githubStatus.github_username}
                  </Typography>
                </Box>
              )}
              {!githubStatus?.connected && !githubLoading && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Not connected
                </Typography>
              )}
            </Box>
          </Box>
          {githubLoading ? (
            <CircularProgress size={20} />
          ) : githubStatus?.connected ? (
            <Button
              variant="outlined"
              size="small"
              onClick={handleDisconnectGitHub}
            >
              Disconnect
            </Button>
          ) : (
            <Button
              variant="contained"
              size="small"
              startIcon={<GitHubIcon />}
              onClick={handleConnectGitHub}
              sx={{
                backgroundColor: '#EF8997',
                '&:hover': { backgroundColor: '#e57383' },
              }}
            >
              Connect GitHub
            </Button>
          )}
        </Box>
      </Paper>

      {/* API Keys */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          backgroundColor: '#0a0a0a',
          border: '1px solid rgba(255, 255, 255, 0.12)',
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          API Keys
        </Typography>
        <ApiKeys embedded />
      </Paper>

      {/* Danger Zone */}
      <Paper
        sx={{
          p: 3,
          backgroundColor: '#0a0a0a',
          border: '1px solid rgba(255, 99, 107, 0.3)',
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'error.main' }}>
          Danger Zone
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              Sign Out
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Sign out of your account on this device
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="error"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
          >
            Logout
          </Button>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 600, color: 'error.main' }}>
              Delete Account
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Permanently delete your account and all data
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="error"
            onClick={() => alert('Contact support@canard.cloud to delete your account')}
          >
            Delete Account
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default Settings;
